// src/components/AgentChatWindow.js
import React, { useState, useRef, useEffect, useMemo } from 'react'; // Added useMemo
import ReactMarkdown from 'react-markdown';
import styles from './AgentChatWindow.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faUserAstronaut,
  faTools,
  faStop,
  faUser,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import AgentJobCard from './AgentJobCard';
import { useJobs } from '../context/jobContext';

const AgentChatWindow = ({ selectedInfluencers }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // We keep this to prevent double-firing within a single session
  const [resumedJobs, setResumedJobs] = useState(new Set());

  const {
    sendAgentChat,
    fetchAgentJobHistory,
    activeAgentChatId,
    setActiveAgentChat,
    getAgentChatHistory,
    addAgentChatToList,
  } = useJobs();

  const messageListRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isNewSessionRef = useRef(false);

  // --- NEW: Calculate which jobs are ALREADY in the history ---
  // The backend saves a message starting with "[SYSTEM EVENT: JOB #123 FINISHED]"
  // We use this to block the AgentJobCard from triggering a re-run on page load.
  const processedJobIds = useMemo(() => {
    const ids = new Set();
    messages.forEach((msg) => {
      // Look for the specific system header used in agent_chat.py
      const match =
        msg.content &&
        msg.content.match(/\[SYSTEM EVENT: JOB #(\d+) FINISHED\]/);
      if (match && match[1]) {
        ids.add(parseInt(match[1]));
      }
    });
    return ids;
  }, [messages]);

  // --- Load History ---
  useEffect(() => {
    const loadHistory = async () => {
      if (isNewSessionRef.current) {
        isNewSessionRef.current = false;
        return;
      }

      if (activeAgentChatId) {
        setIsLoading(true);
        setMessages([]);

        const data = await getAgentChatHistory(activeAgentChatId);

        if (data && data.messages) {
          const uiMessages = data.messages.map((msg) => ({
            role: msg.role === 'model' ? 'model' : 'user',
            content: msg.content,
            tools: msg.meta?.tools || [],
          }));
          setMessages(uiMessages);
        }
        setIsLoading(false);
      } else {
        setMessages([
          {
            role: 'model',
            content:
              'I am ready. Select influencers and tell me what you want to achieve.',
          },
        ]);
      }
    };

    loadHistory();
  }, [activeAgentChatId, getAgentChatHistory]);

  // Scroll to bottom
  useEffect(() => {
    if (messageListRef.current) {
      const { scrollHeight, clientHeight } = messageListRef.current;
      if (scrollHeight > clientHeight) {
        messageListRef.current.scrollTo({
          top: scrollHeight,
          behavior: 'smooth',
        });
      }
    }
  }, [messages]);

  const sendUserMessage = async (payload) => {
    setIsLoading(true);
    abortControllerRef.current = new AbortController();

    if (!payload.resume_job_id) {
      setMessages((prev) => [
        ...prev,
        { role: 'model', content: 'Thinking...', isTemp: true },
      ]);
    }

    try {
      const response = await sendAgentChat(
        { ...payload, chatId: activeAgentChatId },
        abortControllerRef.current.signal,
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Server Error');
      }

      const data = await response.json();

      if (!activeAgentChatId && data.chatId) {
        isNewSessionRef.current = true;
        setActiveAgentChat(data.chatId);
        addAgentChatToList({
          chatId: data.chatId,
          titleChat: input.substring(0, 30) || 'New Agent Session',
          updatedAt: new Date().toISOString(),
        });
      }

      setMessages((prev) => {
        const clean = prev.filter((m) => !m.isTemp);
        const tools = (data.tools_used || []).map((t) => ({
          name: t.name
            .replace(/_/g, ' ')
            .replace('consult the', '')
            .toUpperCase(),
        }));

        fetchAgentJobHistory();

        return [
          ...clean,
          { role: 'model', content: data.content, tools: tools },
        ];
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) => {
          const clean = prev.filter((m) => !m.isTemp);
          return [
            ...clean,
            { role: 'system', content: `Error: ${err.message}` },
          ];
        });
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleResumeJob = async (jobId) => {
    // 1. Check if already processing/processed this specific ID in this session
    if (resumedJobs.has(jobId)) return;

    // 2. Check if the chat history ALREADY contains the System Event for this job
    // (Prevents re-sending event on page reload)
    if (processedJobIds.has(jobId)) return;

    console.log(`[Frontend] Job ${jobId} finished. Signaling Agent...`);

    // Add to Set immediately to prevent double-fire during network wait
    setResumedJobs((prev) => new Set(prev).add(jobId));

    try {
      // Send the "Invisible" system message to the backend
      await sendUserMessage({ resume_job_id: jobId });
    } catch (e) {
      console.error(
        `[Frontend] Failed to signal completion for Job ${jobId}`,
        e,
      );
      // Remove from set so we can retry if needed
      setResumedJobs((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const text = input;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);

    await sendUserMessage({
      message: text,
      selected_influencers: selectedInfluencers.map((inf) => inf.value),
    });
  };

  const renderContent = (content) => {
    // 1. Normalize the content string to force standard format for the splitter
    // Replace "Job ID is 123", "Job: 123", "Job 123" with "Job #123"
    // Regex explanation:
    // Job       -> Literal "Job"
    // [\s\w:.]*? -> Any characters (spaces, letters, colons, dots) non-greedy
    // #?        -> Optional hash
    // \s*       -> Optional space
    // (\d+)     -> Capture the digits
    const normalizedContent = content.replace(
      /Job[\s\w:.]*?#?\s*(\d+)/gi,
      'Job #$1',
    );

    // 2. Now split using the strict standard regex
    const parts = normalizedContent.split(/(Job\s*#\d+)/gi);

    return (
      <div>
        {parts.map((part, i) => {
          // Check if this part is our normalized token
          const match = part.match(/Job\s*#(\d+)/i);
          if (match) {
            return (
              <AgentJobCard
                key={i}
                jobId={parseInt(match[1])}
                onResume={handleResumeJob}
              />
            );
          }
          return <ReactMarkdown key={i}>{part}</ReactMarkdown>;
        })}
      </div>
    );
  };

  const isMissionComplete = messages.some(
    (msg) =>
      msg.role === 'model' &&
      (msg.content.includes('start a **Fresh Chat**') ||
        msg.content.includes('start a **fresh chat**')),
  );

  return (
    <div className={styles.chatContainer}>
      <div className={styles.messageList} ref={messageListRef}>
        {messages.map((msg, idx) => (
          <div key={idx} className={`${styles.message} ${styles[msg.role]}`}>
            {msg.tools?.length > 0 && (
              <div className={styles.toolContainer}>
                {msg.tools.map((t, i) => (
                  <span key={i} className={styles.toolBadge}>
                    <FontAwesomeIcon icon={faTools} /> {t.name}
                  </span>
                ))}
              </div>
            )}
            <div className={styles.bubble}>
              <FontAwesomeIcon
                icon={msg.role === 'model' ? faUserAstronaut : faUser}
                className={styles.icon}
              />
              <div className={styles.content}>
                {msg.role === 'system'
                  ? msg.content
                  : renderContent(msg.content)}
                {msg.isTemp && <span className={styles.cursor}>▋</span>}
              </div>
            </div>
          </div>
        ))}
        {/* --- MISSION COMPLETE BANNER --- */}
        {isMissionComplete && (
          <div className={styles.missionCompleteBanner}>
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>
              All tasks of current conversation completed. Please start a new
              chat for the next task.
            </span>
          </div>
        )}
      </div>
      <form onSubmit={handleSubmit} className={styles.inputForm}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the Agent..."
          disabled={isLoading}
        />
        <button type="submit" disabled={isLoading || !input.trim()}>
          <FontAwesomeIcon icon={isLoading ? faStop : faPaperPlane} />
        </button>
      </form>
    </div>
  );
};

export default AgentChatWindow;
